import React from "react";
import { MessageSquare, X, Send, Check } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { logger } from "@/lib/logger";

export default function CommentsDrawer({ projectId, open, onClose, sendComment, liveComments = [], setLiveComments }) {
  const [comments, setComments] = React.useState([]);
  const [text, setText] = React.useState("");
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!open || !projectId) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/comments/${projectId}`);
        setComments(data);
      } catch (err) {
        logger.warn("comments: initial load failed", err);
      }
      finally { setLoading(false); }
    })();
  }, [open, projectId]);

  // Merge live comments in
  React.useEffect(() => {
    if (liveComments.length === 0) return;
    setComments((existing) => {
      const ids = new Set(existing.map((c) => c.id));
      const merged = [...liveComments.filter((c) => !ids.has(c.id)), ...existing];
      return merged;
    });
    setLiveComments?.([]);
  }, [liveComments, setLiveComments]);

  const submit = () => {
    const body = text.trim();
    if (!body) return;
    sendComment("project", projectId, body);
    setText("");
  };

  const resolve = async (id) => {
    await api.patch(`/comments/${id}/resolve`);
    setComments((cs) => cs.map((c) => (c.id === id ? { ...c, resolved: true } : c)));
    toast.success("Resolved");
  };

  if (!open) return null;

  return (
    <aside className="w-[360px] shrink-0 border-l border-[#1E1E1E] bg-[#0F0F10] flex flex-col h-full" data-testid="comments-drawer">
      <div className="h-14 border-b border-[#1E1E1E] px-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          <MessageSquare size={14} className="text-[#D4AF37]" />
          <span>Comments</span>
        </div>
        <button onClick={onClose} className="text-[#71717A] hover:text-[#FFFFF0]" data-testid="comments-close"><X size={16} /></button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading ? <div className="text-xs text-[#A1A1AA]">Loading…</div> :
          comments.length === 0 ? (
            <div className="text-xs text-[#71717A] italic text-center py-10">No comments yet. Start the conversation.</div>
          ) : (
            comments.map((c) => (
              <div key={c.id} className={`border ${c.resolved ? "border-[#1E1E1E] opacity-50" : "border-[#27272A]"} bg-[#141414] p-3 text-xs`} data-testid={`comment-${c.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[#D4AF37] uppercase tracking-widest text-[10px]">{c.author_name}</span>
                  <span className="text-[#71717A]">{new Date(c.created_at).toLocaleString()}</span>
                </div>
                <div className="text-[#E4E4E7] leading-relaxed">{c.body}</div>
                {!c.resolved && (
                  <button onClick={() => resolve(c.id)}
                    className="mt-2 text-[10px] uppercase tracking-widest text-[#71717A] hover:text-[#D4AF37] inline-flex items-center gap-1"
                    data-testid={`comment-resolve-${c.id}`}>
                    <Check size={10} /> Resolve
                  </button>
                )}
              </div>
            ))
          )
        }
      </div>

      <div className="border-t border-[#1E1E1E] p-3">
        <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)}
          placeholder="Leave a note for your team…"
          className="w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#D4AF37] focus:outline-none text-[#E4E4E7] p-2.5 text-xs placeholder-[#71717A]"
          data-testid="comment-input" />
        <button onClick={submit} disabled={!text.trim()}
          className="mt-2 w-full bg-[#D4AF37] text-[#0A0A0A] py-2 text-xs font-medium hover:bg-[#F59E0B] disabled:opacity-50 inline-flex items-center justify-center gap-2 transition-colors"
          data-testid="comment-submit">
          <Send size={12} /> Send
        </button>
      </div>
    </aside>
  );
}
